import { FC } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import ScheduleIcon from '@mui/icons-material/Schedule'
// TODO: re-enable when webhook trigger is wired up on the backend (endpoint + auth).
// import WebhookIcon from '@mui/icons-material/Webhook'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import HubIcon from '@mui/icons-material/Hub'
import { TriggerNodeData, WorkflowNodeData } from '../types'
import { themeColor } from '../../../layout/theme/color'
import McpInputSchemaBuilder from './McpInputSchemaBuilder'

interface TriggerConfigProps {
  data: TriggerNodeData
  onUpdate: (data: Partial<WorkflowNodeData>) => void
}

const TriggerConfig: FC<TriggerConfigProps> = ({ data, onUpdate }) => {
  const translate = useTranslate()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 600, color: themeColor.slate60, mb: 0.5, display: 'block' }}>
          Type
        </Typography>
        <ToggleButtonGroup
          value={data.triggerType}
          exclusive
          onChange={(_e, val: string | null) => val && onUpdate({ triggerType: val as 'cron' | 'webhook' | 'manual' | 'mcp' })}
          size="small"
          fullWidth
        >
          <Tooltip title={translate('workflow.nodes.cron')} arrow>
            <ToggleButton value="cron" sx={{ textTransform: 'none', minWidth: 40 }}>
              <ScheduleIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </Tooltip>
          {/* TODO: re-enable when webhook trigger is wired up on the backend (endpoint + auth).
          <Tooltip title={translate('workflow.nodes.webhook')} arrow>
            <ToggleButton value="webhook" sx={{ textTransform: 'none', minWidth: 40 }}>
              <WebhookIcon sx={{ fontSize: 16 }} />
            </ToggleButton>
          </Tooltip>
          */}
          <Tooltip title={translate('workflow.nodes.manual')} arrow>
            <ToggleButton value="manual" sx={{ textTransform: 'none', minWidth: 40 }}>
              <PlayArrowIcon sx={{ fontSize: 20 }} />
            </ToggleButton>
          </Tooltip>
          <Tooltip title={translate('workflow.nodes.mcp')} arrow>
            <ToggleButton value="mcp" sx={{ textTransform: 'none', minWidth: 40 }}>
              <HubIcon sx={{ fontSize: 16 }} />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>

      <TextField
        label={translate('workflow.config.label')}
        value={data.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        size="small"
        fullWidth
      />

      {data.triggerType === 'cron' && (
        <TextField
          label={translate('workflow.config.cronExpression')}
          value={data.cronExpression ?? ''}
          onChange={(e) => onUpdate({ cronExpression: e.target.value })}
          size="small"
          fullWidth
          placeholder="0 8 * * 1-5"
          helperText="min hour day month weekday"
        />
      )}

      {/* TODO: re-enable when webhook trigger is wired up on the backend (endpoint + auth).
      {data.triggerType === 'webhook' && (
        <TextField
          label={translate('workflow.config.webhookPath')}
          value={data.webhookPath ?? ''}
          onChange={(e) => onUpdate({ webhookPath: e.target.value })}
          size="small"
          fullWidth
          placeholder="/webhooks/my-trigger"
        />
      )}
      */}

      {data.triggerType === 'mcp' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            variant="caption"
            sx={{ color: themeColor.slate60, display: 'block', lineHeight: 1.4 }}
          >
            {translate('workflow.config.mcpHelp')}
          </Typography>

          <Divider sx={{ my: 0.5 }} />

          <TextField
            label={translate('workflow.config.mcpToolName')}
            value={data.mcpToolName ?? ''}
            onChange={(e) => onUpdate({ mcpToolName: e.target.value })}
            size="small"
            fullWidth
            placeholder="list_processes"
            helperText={translate('workflow.config.mcpToolNameHelp')}
            InputProps={{
              sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 },
            }}
          />

          <TextField
            label={translate('workflow.config.mcpToolDescription')}
            value={data.mcpToolDescription ?? ''}
            onChange={(e) => onUpdate({ mcpToolDescription: e.target.value })}
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder={translate('workflow.config.mcpToolDescriptionPlaceholder')}
            helperText={translate('workflow.config.mcpToolDescriptionHelp')}
          />

          <McpInputSchemaBuilder
            value={data.mcpToolInputSchema}
            onChange={(next) => onUpdate({ mcpToolInputSchema: next })}
          />
        </Box>
      )}
    </Box>
  )
}

export default TriggerConfig
